/**
 * The two plan-assignment reads more than one surface needs, as `queryOptions`.
 *
 * Both are keyed by the GENERATED key functions, not by a key of our own, and that is the point:
 * `pages/assigned-plan-calendar` and `pages/client-calendar` read the same two endpoints through the
 * generated hooks, and a second key here would have given one plan two cache entries — a task card
 * that added a week would then leave the plan calendar showing the old end. One key, one entry,
 * whichever caller asked first.
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';

import {
  getGetPlanAssignmentCustomerCustomerIdQueryKey,
  getGetPlanAssignmentIdExecutionSummaryQueryKey,
  getGetPlanAssignmentIdOverviewQueryKey,
  getPlanAssignmentCustomerCustomerId,
  getPlanAssignmentIdExecutionSummary,
} from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * How the client executed one assignment: every cycle's slots with their status, the counts and the
 * tonnage. `lang` is the content language — the slots carry exercise names the server localises.
 */
export function executionSummaryQueryOptions(
  assignmentId: number,
  lang: ContentLanguage,
  { enabled = true }: { readonly enabled?: boolean } = {},
) {
  const params = { lang };
  return queryOptions({
    queryKey: getGetPlanAssignmentIdExecutionSummaryQueryKey(assignmentId, params),
    queryFn: ({ signal }) =>
      getPlanAssignmentIdExecutionSummary(assignmentId, params, undefined, signal),
    enabled: enabled && assignmentId > 0,
  });
}

/** Every assignment one client holds or held, newest and oldest alike, with the plan's cover. */
export function customerAssignmentsQueryOptions(
  customerId: number,
  { enabled = true }: { readonly enabled?: boolean } = {},
) {
  return queryOptions({
    queryKey: getGetPlanAssignmentCustomerCustomerIdQueryKey(customerId),
    queryFn: ({ signal }) => getPlanAssignmentCustomerCustomerId(customerId, undefined, signal),
    enabled: enabled && customerId > 0,
  });
}

/**
 * **Everything this app reads about how one assignment was executed, marked stale** — the execution
 * summary (the plan calendar, the attention queue, the client calendar's walk) and the plan's
 * overview (Przegląd), in every content language.
 *
 * One function because every write that changes what the client has, did or will do changes BOTH
 * reads, and seven writers each naming the keys they remembered is how the overview went on showing
 * a moved session's old week (27 September 2026). The generated keys without `params` are PREFIXES
 * of every language's entry.
 */
export function invalidateAssignmentExecution(queryClient: QueryClient, assignmentId: number): void {
  void queryClient.invalidateQueries({
    queryKey: getGetPlanAssignmentIdExecutionSummaryQueryKey(assignmentId),
  });
  void queryClient.invalidateQueries({ queryKey: getGetPlanAssignmentIdOverviewQueryKey(assignmentId) });
}
